import { useTranslation } from "react-i18next";
import { useForgotPasswordMutation } from "../api/userAuthApi";
import { useMutationWithFeedback } from "../../../common/hooks/useMutationWithFeedback";
import { useLanguage } from "../../../common/features/language/hooks/useLanguage";
import type { ForgotPasswordPayload } from "../types/userAuthApiTypes";

export const useForgotPassword = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [forgotPassword, result] = useForgotPasswordMutation();

  const {
    trigger: triggerMutation,
    isLoading,
    isSuccess,
  } = useMutationWithFeedback<{ message: string }, ForgotPasswordPayload>({
    mutation: [forgotPassword, result],
    loadingMessage: t(
      "feedback.user.forgotPassword.loading",
      "Sending password reset link..."
    ),
    successMessage: t(
      "feedback.user.forgotPassword.success",
      "If an account exists, a reset link has been sent."
    ),
    errorMessages: {
      default: t(
        "feedback.user.forgotPassword.error",
        "Could not send reset link. Please try again."
      ),
      // Map both snake_case and kebab-case for robustness
      invalid_email: t(
        "user.auth.invalidEmailFormat",
        "Please provide a valid email address."
      ),
      "invalid-email": t(
        "user.auth.invalidEmailFormat",
        "Please provide a valid email address."
      ),
    },
    successToastType: "success",
  });

  /**
   * Wrapper to inject the current language code into the payload.
   */
  const handleForgotPassword = async (data: ForgotPasswordPayload) => {
    // We create a new payload object that includes the optional 'lang' property
    return triggerMutation({ ...data, lang: currentLanguage });
  };

  return {
    handleForgotPassword,
    isLoading,
    isSuccess,
  };
};
